import React, { useState, useEffect, useCallback, useRef } from 'react';
import emailjs from '@emailjs/browser';
import Banner from '../../components/Banner/Banner';
import Intro from '../../components/Intro/Intro';
import Icon from '../../components/Icon/Icon';
import ScrollAnimatedSection from '../../components/ScrollAnimatedSection/ScrollAnimatedSection';
import SectionParticles from '../../components/SectionParticles/SectionParticles';
import SectionReveal from '../../components/SectionReveal/SectionReveal';
import styles from './Home.module.css';
import kan from '../../assets/kan_guarida.webp';
import paisaje1 from '../../assets/Paisajemc.webp';
import vacio from '../../assets/el_vacio.webp';
import storm from '../../assets/WitherStorm.webp';
import pueblo from '../../assets/pueblo_tecniland.webp';
import castillosky from '../../assets/Castillo_flotante.webp';
import thormento from '../../assets/thormento.webp';

/**
 * Home Module - TECNILAND Main Page
 * DESKTOP-FIRST: Todas las secciones optimizadas para 1920px+
 * CSS Modules para aislamiento de estilos
 */
function Home() {
  // Estados para diferentes secciones
  const [activeFeatureTab, setActiveFeatureTab] = useState('vision');
  const [selectedModpack, setSelectedModpack] = useState(null);
  const [activeLoreTab, setActiveLoreTab] = useState('intro');
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [selectedPersonaje, setSelectedPersonaje] = useState(null);
  const [selectedMission, setSelectedMission] = useState(null);
  const [expandedRoadmapItem, setExpandedRoadmapItem] = useState(null);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
  const [showLightboxText, setShowLightboxText] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [formStage, setFormStage] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  // Refs para modales (para scrollIntoView)
  const characterModalRef = useRef(null);
  const personajeModalRef = useRef(null);
  const missionModalRef = useRef(null);
  const modpackModalRef = useRef(null);
  const galleryModalRef = useRef(null);
  
  // Ref para guardar el elemento activo antes de abrir modal (focus trap básico)
  const previousFocusRef = useRef(null);

  // ==================== MODAL UX: ESC to close, scroll listener ====================
  
  // Close all modals function
  const closeAllModals = useCallback(() => {
    setSelectedModpack(null);
    setSelectedCharacter(null);
    setSelectedPersonaje(null);
    setSelectedMission(null);
    setSelectedGalleryImage(null);
  }, []);

  // ESC key listener to close modals
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeAllModals();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeAllModals]);

  // Prevent body scroll when ANY modal is open (mejor UX en mobile)
  useEffect(() => {
    if (selectedCharacter || selectedPersonaje || selectedMission || selectedModpack || selectedGalleryImage) {
      // Guardar scroll actual para restaurarlo después
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      // Añadir clase para ocultar navbar en mobile
      document.body.classList.add('modal-open');
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.classList.remove('modal-open');
        window.scrollTo(0, scrollY);
      };
    }
  }, [selectedCharacter, selectedPersonaje, selectedMission, selectedModpack, selectedGalleryImage]);

  // Scroll modal into view when opened - CONSOLIDADO en un solo efecto
  // También maneja focus trap básico (guardar y restaurar focus)
  useEffect(() => {
    // Mapa de estados a refs para evitar repetición
    const modalConfig = [
      { state: selectedCharacter, ref: characterModalRef },
      { state: selectedPersonaje, ref: personajeModalRef },
      { state: selectedMission, ref: missionModalRef },
      { state: selectedModpack, ref: modpackModalRef },
      { state: selectedGalleryImage, ref: galleryModalRef },
    ];
    
    // Verificar si hay algún modal abierto
    const anyModalOpen = modalConfig.some(m => m.state);
    
    if (anyModalOpen) {
      // Guardar el elemento activo actual antes de abrir el modal
      previousFocusRef.current = document.activeElement;
      
      // Encontrar el modal activo y hacer scroll + focus
      const activeModal = modalConfig.find(m => m.state && m.ref.current);
      if (activeModal) {
        activeModal.ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Focus al primer botón cerrar del modal
        const closeButton = activeModal.ref.current.querySelector('button');
        if (closeButton) {
          setTimeout(() => closeButton.focus(), 100);
        }
      }
    } else {
      // Restaurar focus al elemento anterior cuando se cierra el modal
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus();
        previousFocusRef.current = null;
      }
    }
    
    // Reset lightbox text cuando se cierra el modal de galería
    if (!selectedGalleryImage) {
      setShowLightboxText(true);
    }
  }, [selectedCharacter, selectedPersonaje, selectedMission, selectedModpack, selectedGalleryImage]);

  // Close modals when scrolling out of Lore section (con throttle para performance)
  useEffect(() => {
    let lastScrollTime = 0;
    const throttleMs = 100; // Ejecutar máximo cada 100ms
    
    const handleScroll = () => {
      const now = Date.now();
      if (now - lastScrollTime < throttleMs) return;
      lastScrollTime = now;
      
      // Only check if in Lore section and a modal is open
      if (selectedCharacter || selectedPersonaje || selectedMission) {
        const loreSection = document.getElementById('lore');
        if (!loreSection) return;
        
        const rect = loreSection.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        
        // Close modal if Lore section is completely out of view
        const isCompletelyAbove = rect.bottom < 0;
        const isCompletelyBelow = rect.top > viewportHeight;
        
        if (isCompletelyAbove || isCompletelyBelow) {
          setSelectedCharacter(null);
          setSelectedPersonaje(null);
          setSelectedMission(null);
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [selectedCharacter, selectedPersonaje, selectedMission]);

  // ==================== DATA ====================

  // Features/Launcher data
  const features = [
    {
      id: 1,
      icon: 'modpacks',
      title: 'Gestor de Modpacks',
      description: 'Descarga, actualiza y gestiona múltiples modpacks con un clic',
    },
    {
      id: 2,
      icon: 'profile',
      title: 'Perfiles Personalizados',
      description: 'Crea perfiles únicos con configuraciones específicas por modpack',
    },
    {
      id: 3,
      icon: 'auto-update',
      title: 'Actualizaciones Automáticas',
      description: 'Recibe nuevas versiones sin intervención manual',
    },
    {
      id: 4,
      icon: 'gamepad',
      title: 'Lanzador Integrado',
      description: 'Ejecuta Minecraft con tu modpack configurado al instante',
    },
    {
      id: 5,
      icon: 'stats',
      title: 'Estadísticas',
      description: 'Monitorea tu progreso, horas jugadas y logros',
    },
    {
      id: 6,
      icon: 'settings',
      title: 'Configuración Avanzada',
      description: 'Control total sobre RAM, JVM args y ajustes de rendimiento',
    },
  ];

  // Modpacks data
  const modpacks = [
    {
      id: 1,
      name: 'TECNILAND ORIGINS',
      icon: 'cosmic',
      image: null,
      version: '1.16.5',
      mods: 80,
      description: 'La esencia original: caos divertido, jefes extraños y eventos inesperados que dan vida al mundo',
      features: ['Eventos caóticos', 'Jefes impredecibles', 'Magia desbalanceada', 'Objetos absurdamente divertidos'],
      status: 'MUY PRONTO DISPONIBLE',
    },
    {
      id: 2,
      name: 'TECNILAND METRIC EFUSSION',
      icon: 'mountain',
      image: null,
      version: '1.18.2',
      mods: 130,
      description: 'Supervivencia realista con un enfoque oscuro y terror psicológico en cada rincón.',
      features: ['Supervivencia realista', 'Terror ambiental', 'IA hostil mejorada', 'Recursos escasos y tensión constante'],
      status: 'MUY PRONTO DISPONIBLE',
    },
    {
      id: 3,
      name: 'EVIL OF THE SOUL',
      icon: 'magic',
      image: null,
      version: '1.16.5',
      mods: 100,
      description: 'Posapocalíptico, hostil y marcado por una guerra perdida. La supervivencia es la única ley',
      features: ['Entorno posapocalíptico', 'Facciones enemigas', 'Supervivencia extrema', 'Historia ambiental oscura'],
      status: 'MUY PRONTO DISPONIBLE',
    },
    {
      id: 4,
      name: 'TECNILAND ENDING CRACKS',
      icon: 'tech-advanced',
      image: null,
      version: '1.20.1',
      mods: 200,
      description: 'Un mundo tecnológico colapsado donde la misión es reconstruir, restaurar y sobrevivir entre ruinas.',
      features: ['Tecnología avanzada', 'Mundo colapsado', 'Restauración progresiva', 'Exploración en ruinas tecnológicas'],
      status: 'MUY PRONTO DISPONIBLE',
    },
    {
      id: 5,
      name: 'TECNILAND DEFINITIVE EDITION',
      icon: 'dungeon',
      image: null,
      version: '1.20.1',
      mods: 140,
      description: 'Fusión de múltiples realidades de TECNILAND: tragedia, combate avanzado y mundos entrelazados.',
      features: ['Multiversos combinados', 'Sistema de combate avanzado', 'Misiones interdimensionales', 'Eventos globales especiales'],
      status: 'MUY PRONTO DISPONIBLE',
    },
    {
      id: 6,
      name: 'TECNILAND',
      icon: 'nature',
      image: null,
      version: '???',
      mods: '???',
      description: 'La historia definitiva… pronto revelada.',
      features: ['Lore central unificado', 'Mecánicas inéditas', 'Evolución del mundo', '??? (Aún por revelar)'],
      status: 'MUY PRONTO DISPONIBLE',
    },
  ];

  // Lore characters
  const characters = [
    {
      id: 1,
      name: 'Ppkeash',
      role: 'El Creador',
      icon: 'ppkeash',
      featuredMission: 'Fundar TECNILAND y establecer el primer Nexus',
      description: 'El visionario que descubrió el portal a TECNILAND. Su objetivo: crear un universo donde la creatividad y la tecnología convergen.',
      story: 'Después de años explorando mundos, encontro algo extraordinario: una dimensión donde las leyes de la realidad pueden ser reescritas. TECNILAND nació de su determinación y ahora carga con el peso del orden. Constructor de alianzas, fortificaciones y decisiones crítica.',
    },
    {
      id: 2,
      name: 'Kiara',
      role: 'Hechicera del Eclipse',
      icon: 'magic',
      featuredMission: 'Controlar el ritual lunar sin destruir, ni ser consumida por la luna',
      description: 'Maldita y bendecida por la luna. Entre la luz y la sombra, su poder es tan hermoso como peligroso.',
      story: 'Durante un ritual fallido, Kiara quedó vinculada a la energía lunar del Nexoverso. Su poder crece o se descontrola según la fase del eclipse. Aunque intenta mantenerse estable, hay noches donde su magia amenaza con liberarse. Investiga cómo romper la conexión antes de que la luna la consuma por completo.',
    },
    {
      id: 3,
      name: 'Alan',
      role: 'El Mago Errante',
      icon: 'alainorlu',  
      featuredMission: 'Descifrar las runas de los mundos caídos',
      description: 'Portador de antiguas runas y saber oculto. Ha caminado por más dimensiones de las que admite.',
      story: 'Caminó por mundos que ya no existen... cada uno le dejó un símbolo rúnico en el cuerpo que le otorga poder, pero también visiones de los mundos que murieron. Busca comprender por qué algunos universos sobreviven y otros no, y teme que TECNILAND sea el siguiente en caer.',
    },
    {
      id: 4,
      name: 'MicroJD',
      role: 'El Arquitecto Tecnológico',
      icon: 'microjd',
      featuredMission: 'Construir el primer reactor de energía Nexus',
      description: 'Aventurero incansable con tecnologia que da rienda a los rincones desconocidos de TECNILAND.',
      story: 'Viene de una línea de ingenieros que estudiaron energías antiguas. MicroJD descubrió cómo conectar tecnología con energía del Nexus sin causar colapso. Gracias a sus máquinas, TECNILAND pudo prosperar. Aún investiga artefactos perdidos que podrían restaurar o destruir el mundo si caen en malas manos.',
    },
    {
      id: 5,
      name: 'Bruno',
      role: 'El Portador de la Falla',
      icon: 'bruno',
      featuredMission: 'Cerrar la grieta dimensional del norte',
      description: 'Guerrero marcado por la energía de una grieta dimensional; camina entre el poder y el riesgo de romper el mundo con cada paso.',
      story: 'Cuando una grieta dimensional se abrió en medio del reino, Bruno fue el único que intentó cerrarla. La energía lo marcó profundamente, fusionando parte de la Falla con su cuerpo.. Desde entonces lucha contra impulsos y voces que intentan controlarlo. Usa su poder para el bien, pero sabe que si pierde el control, podría romper el mundo.',
    },
    {
      id: 6,
      name: 'Foxtans',
      role: 'El Comerciante Interdimensional',
      icon: 'foxtans',
      featuredMission: 'Establecer rutas comerciales entre dimensiones',
      description: 'Aventurero incansable con tecnologia que da rienda a los rincones desconocidos de TECNILAND.',
      story: 'Viene de una línea de ingenieros que estudiaron energías antiguas. MicroJD descubrió cómo conectar tecnología con energía del Nexus sin causar colapso. Gracias a sus máquinas, TECNILAND pudo prosperar. Aún investiga artefactos perdidos que podrían restaurar o destruir el mundo si caen en malas manos.',
    },
    {
      id: 7,
      name: 'Sebas',
      role: 'El Cazador de Almas',
      icon: 'sebas',
      featuredMission: 'Rastrear la criatura de Sorin antes del colapso',
      description: 'Rastreador silencioso capaz de percibir entidades invisibles; donde otros ven sombras, él encuentra amenazas que deben caer.',
      story: 'Nació con la capacidad de percibir entidades que la mayoría no ve. Cuando la criatura de Sorin comenzó a influir en TECNILAND, Sebas fue quien detectó su presencia antes que nadie. Ha viajado por mazmorras, ruinas y zonas corruptas eliminando criaturas que podrían abrir portales indeseados.',
    },
    {
      id: 8,
      name: 'Akimitzu',
      role: 'La Protectora de Gaia',
      icon: 'akimitzu',
      featuredMission: 'Purificar el bosque corrupto del este',
      description: 'Guía espiritual conectada al pulso vivo del mundo; cura tierras heridas y restaura la naturaleza que las grietas intentan destruir.',
      story: 'Sentía la vida del mundo como si fuera parte de ella. Cuando portales comenzaron a alterar la naturaleza, Akimitzu fue la primera en notar que el reino estaba “enfermo”. Puede curar tierras corrompidas y plantas mutadas por energía abisal. Su misión es mantener el balance que las grietas dimensionales intentan destruir.',
    },
    {
      id: 9,
      name: 'Matt',
      role: 'El Exiliado de Nyctocosm',
      icon: 'matt',
      featuredMission: 'Escapar de Nyctocosm sin perder el alma',
      description: 'Errante atrapado entre luz y oscuridad; su alma dividida refleja la eterna lucha contra la sombra que nunca dejó de perseguirlo.',
      story: 'Nyctocosm era una dimensión donde la oscuridad tenía conciencia propia. Matt la desafió intentando liberar a su gente y fue expulsado. La sombra lo siguió y marcó su alma, dejándolo atrapado entre luz y oscuridad. Llegó a TECNILAND buscando redención… o un lugar donde su maldición no lo destruyera.',
    },
    {
      id: 10,
      name: 'Jagger',
      role: 'El Archivista del Vacío',
      icon: 'jagger',
      featuredMission: 'Preservar el conocimiento prohibido del Vacío',
      description: 'Erudito de mundos perdidos que guarda secretos imposibles; su sabiduría pesa tanto como el precio de conocerla.',
      story: 'Pasó siglos registrando secretos de mundos perdidos. Cuando el Vacío consumió su biblioteca infinita, Jagger escapó con fragmentos de conocimiento prohibido. Ahora escribe el nuevo capítulo del Nexoverso, advirtiendo que la misma fuerza que destruyó su archivo podría estar aproximándose.',
    },
    {
      id: 11,
      name: 'Arkniz',
      role: 'La Marionetista de Sombras',
      icon: 'arkniz',
      featuredMission: 'Dominar los hilos que conectan las emociones',
      description: 'Ilusionista que controla emociones y criaturas oscuras; su magia danza entre la elegancia y el peligro absoluto.',
      story: 'Creció en una aldea donde las emociones se representaban con criaturas mágicas. Tras un ataque de entidades abisales, descubrió que podía manipular los “hilos” que conectan sentimientos y seres. Viajó a TECNILAND buscando controlar ese poder… antes de que las sombras la controlaran a ella.',
    },
    {
      id: 12,
      name: 'Levitan',
      role: 'El Heredero de los Dragones',
      icon: 'levitan',
      featuredMission: 'Recuperar el Fuego Primordial de los dragones',
      description: 'Último descendiente de un reino dracónico desaparecido; porta una fuerza ancestral que despierta cuando el mundo está en peligro.',
      story: 'Viene del antiguo Reino de los Dragones, un lugar que colapsó cuando grietas abisales devoraron sus cielos. Fue enviado a TECNILAND con el último fragmento del Fuego Primordial, la herencia de su linaje. Ahora busca rastros de los dragones perdidos y la causa real de la caída de su reino, mientras una fuerza ancestral despierta dentro de él.',
    },
    {
      id: 13,
      name: 'SushiOWO',
      role: 'El Navegante Abisal',
      icon: 'sushiowo',
      featuredMission: 'Explorar las ruinas sumergidas del Abyss',
      description: 'Explorador del océano profundo que persigue ruinas sumergidas; sus travesías revelan misterios que el mar trata de ocultar.',
      story: 'Una expedición marítima lo llevó a encontrar una ciudad hundida que no pertenecía este mundo. Allí vio símbolos del Abyss y criaturas que jamás debieron despertar. Desde entonces explora océanos, grietas y ruinas tratando de comprender por qué el mar es el primer lugar donde las dimensiones empiezan a morir.',
    },
    {
      id: 14,
      name: 'Figaro',
      role: 'Domadora de Criaturas',
      icon: 'figaro',
      featuredMission: 'Salvar las criaturas corrompidas del reino',
      description: 'Guardián de la fauna mágica que entiende a las bestias mejor que a los humanos; donde otros ven monstruos, ella encuentra aliados.',
      story: 'Criada entre mobs mágicos, aprendió a comunicarse con ellos y a protegerlos. Cuando criaturas corrompidas comenzaron a aparecer, Figaro notó que muchas estaban “rotas” por energía abisal. Su misión es salvarlas y descubrir qué fuerza está mutando la vida del mundo.',
    },
    {
      id: 15,
      name: 'Kipper',
      role: 'El Guerrero del Tormento',
      icon: 'kipper',
      featuredMission: 'Descubrir el origen de el ojo del Abyss',
      description: 'Combatiente forjado en dolor y gloria; su fuerza surge de una reliquia que convirtió su tormento en poder imparable.',
      story: 'Encontró una reliquia que prometía poder y terminó marcado por un tormento eterno. En vez de rendirse, convirtió ese dolor en su arma. Recorre arenas, ruinas y portales enfrentando criaturas que otros temen. Busca la verdad detrás de la reliquia y quién la creó.',
    },
  ];

  // Personajes del Lore (distintos de los Forjadores)
  const lorePersonajes = [
    {
      id: 1,
      name: 'Sorin',
      icon: 'cosmic',
      description: 'El primer superviviente. Llegó a través del portal desde un mundo colapsado.',
      importance: 'Catalizador del Nexoverso',
      highlight: 'Desapareció dejando solo una criatura tras él',
      story: 'Sorin era un explorador dimensional, el primero en descubrir los Nexus. Antes de desaparecer, dejó pistas sobre la verdadera naturaleza del multiverso y advirtió sobre La Criatura. Algunos dicen que aún vive, atrapado en el Abyss, observando. Otros creen que se fusionó con el Nexus mismo. Su legado permanece en cada portal que se abre, en cada dimensión conectada. La Gema del Recuerdo contiene fragmentos de su consciencia, susurros de advertencias sobre lo que está por venir.',
    },
    {
      id: 2,
      name: 'Eryndra',
      icon: 'magic',
      description: 'Guardiana del Nexus de TECNILAND. Protege el corazón de este mundo.',
      importance: 'Protectora del equilibrio dimensional',
      highlight: 'Puede sentir cuando otros Nexus colapsan',
      story: 'Eryndra descubrió el Nexus de TECNILAND cuando este estaba al borde del colapso. Con magia antigua y sacrificios inconcebibles, logró estabilizarlo... pero a un costo. Ahora está vinculada permanentemente al Nexus; si muere, el portal se cierra para siempre. Posee conocimientos sobre Los Antiguos y ha visto ecos del futuro. Sabe que la batalla final se acerca, y que TECNILAND será el último bastión o la última tumba del Nexoverso.',
    },
    {
      id: 3,
      name: 'La Criatura',
      icon: 'dungeon',
      description: 'Entidad indescriptible que cruzó tras Sorin. Deja cicatrices en cada mundo.',
      importance: 'Amenaza primordial del Nexoverso',
      highlight: 'Un rugido que solo los marcados pueden oír',
      story: 'Nadie sabe qué es realmente. Algunos la llaman "El Eco del Vacío", otros simplemente "La Tormenta". Cruzó detrás de Sorin y desde entonces acecha entre dimensiones. No es malvada en el sentido tradicional; es una fuerza de entropía, un depredador dimensional que se alimenta de la estabilidad de los Nexus. Cada mundo que toca queda marcado con grietas abisales, portales rotos que sangran energía oscura. Su mera presencia corrompe la realidad. Y está acercándose a TECNILAND.',
    },
    {
      id: 4,
      name: 'El Vacío',
      icon: 'cosmic',
      description: 'Una dimensión muerta que guarda la Gema del Recuerdo.',
      importance: 'Testigo de mundos caídos',
      highlight: 'La única pista del colapso dimensional',
      story: 'El Vacío existía antes que el Nexoverso. Es la ausencia absoluta, el anti-espacio donde las leyes de la física no aplican. Cuando Sorin abrió el primer portal, accidentalmente conectó el Nexoverso con el Vacío. Ahora, lentamente, el Vacío se filtra a través de las grietas abisales, consumiendo dimensiones desde adentro. El Abyss es solo su manifestación parcial. Si alguna vez cruza completamente... el Nexoverso dejará de existir.',
    },
    {
      id: 5,
      name: 'Los Antiguos',
      icon: 'ruins',
      description: 'Civilización perdida que construía los Nexus antes del primer colapso.',
      importance: 'Creadores de la tecnología Nexus',
      highlight: 'Sus ruinas esconden secretos imposibles',
      story: 'Los Antiguos crearon los Nexus como puentes entre realidades, buscando la perfección universal. Desarrollaron tecnología que fusiona magia y ciencia a niveles imposibles. Pero algo salió terriblemente mal. En un evento conocido como "El Gran Silencio", todos desaparecieron simultáneamente. Sus ruinas permanecen en dimensiones olvidadas, llenas de artefactos peligrosos y advertencias crípticas. Algunos teorizan que descubrieron al Vacío... y fueron consumidos. Otros creen que ascendieron a una forma de existencia superior. La verdad podría ser peor que ambas.',
    },
    {
      id: 6,
      name: 'El Ojo del Abyss',
      icon: 'eye',
      description: 'Entidad que observa desde las profundidades. Nunca parpadea.',
      importance: 'Vigilante de las grietas abisales',
      highlight: 'Ve a través de dimensiones sin cruzarlas',
      story: 'El Ojo no es un ser físico, sino una consciencia distribuida a través de todas las grietas abisales. Observa, registra, espera. Algunos creen que es un sistema de vigilancia dejado por Los Antiguos. Otros piensan que es una manifestación del Vacío mismo, espiando las dimensiones que planea consumir. Aquellos que han sentido su mirada describen una sensación de ser completamente comprendidos, diseccionados mentalmente sin piedad. El Ojo sabe todos los secretos, todas las posibilidades. Y lo que busca... nadie lo sabe.',
    },
  ];

  const missions = [
    {
      id: 1,
      title: 'La Osadía del Superviviente',
      icon: 'shield',
      description: 'Una misión épica de supervivencia donde debes enfrentarte a los peligros primordiales de TECNILAND.',
      status: 'Completada',
      reward: '5000 XP + Armadura Legendaria',
      story: 'Esta fue la primera gran prueba. Los Forjadores se adentraron en territorios hostiles donde la realidad misma se desmoronaba. Enfrentaron criaturas corrompidas por energía abisal, tormentas dimensionales y ruinas de Los Antiguos llenas de trampas mortales. La supervivencia no era opcional: era el precio de entrada a TECNILAND. Aquellos que completaron esta misión demostraron que tenían la fortaleza necesaria para lo que vendrá. La Armadura Legendaria obtenida está imbuida con protección contra anomalías dimensionales.',
    },
    {
      id: 2,
      title: 'The Last Sword',
      icon: 'sword',
      description: 'Busca la espada legendaria perdida hace eras. Rumores dicen que está en el corazón del mundo antiguo.',
      status: 'Completada',
      reward: '3000 XP + Arma Épica',
      story: 'La Última Espada no es solo un arma; es un fragmento de la voluntad de Los Antiguos. Forjada antes del Gran Silencio, se dice que puede cortar las grietas abisales y sellar portales corruptos. Los Forjadores viajaron a las Ruinas del Mundo Antiguo, enfrentando guardianes mecánicos y pruebas de ingenio imposibles. Al final, descubrieron que la espada estaba esperando... esperando a alguien digno. Ahora está en manos de los héroes, lista para la batalla final contra La Criatura.',
    },
    {
      id: 3,
      title: 'Devuelta a TECNILAND',
      icon: 'home',
      description: 'La misión central. Vuelve a casa después de explorar los confines del universo. Todo converge aquí.',
      status: 'Pendiente',
      reward: 'Verdad Final + Poder Absoluto',
      story: 'Esta es la misión que cambiará todo. Después de explorar dimensiones lejanas, descubrir secretos de Los Antiguos y enfrentar manifestaciones del Vacío, los Forjadores deben regresar a TECNILAND. Pero el hogar que conocían ya no existe: el Nexus está al borde del colapso, Eryndra lucha por mantenerlo estable, y La Criatura se acerca. Esta misión culminará en la revelación final: qué pasó realmente con Sorin, qué es el Vacío, y si TECNILAND puede salvarse... o si debe ser sacrificado para salvar el Nexoverso. La Verdad Final esperará. El Poder Absoluto tendrá un precio.',
    },
  ];

  // Roadmap data
  const roadmapItems = [
    {
      id: 1,
      phase: 'Fase 1',
      title: 'Alpha Cerrada',
      date: 'Q4 2025',
      status: 'En Progreso',
      icon: 'phase-alpha',
      color: '#00ff00',
      milestones: [
        'Lanzamiento de TECNILAND Nexus (Launcher)',
        'Primer modpack completo',
        'Sistema de roles en servidor',
        'Website completa',
      ],
    },
    {
      id: 2,
      phase: 'Fase 2',
      title: 'Beta Pública',
      date: 'Q1 2026',
      status: 'Planificado',
      icon: 'phase-beta',
      color: '#ffc107',
      milestones: [
        'Acceso a beta pública limitado',
        'Primer evento comunitario',
        'Feedback y iteraciones',
        'Segundo modpack temático',
      ],
    },
    {
      id: 3,
      phase: 'Fase 3',
      title: 'Release 1.0',
      date: 'Q2 2026',
      status: 'Futuro',
      icon: 'phase-release',
      color: '#00ff00',
      milestones: [
        'Lanzamiento oficial',
        'Soporte multiplataforma',
        'Sistema de economía avanzado',
      ],
    },
    {
      id: 4,
      phase: 'Fase 4',
      title: 'Expansión Global',
      date: 'Q3 2027+',
      status: 'Visión',
      icon: 'phase-global',
      color: '#00bfff',
      milestones: [
        'Servidores en múltiples regiones',
        'Soporte multiidioma',
        'Colaboraciones con creadores',
        'Merch y físicos de TECNILAND',
      ],
    },
  ];

  // Gallery screenshots - CON URLs de imágenes de fondo
  const screenshots = [
    {
      id: 1,
      title: 'La guarida de KAN',
      icon: 'celestial',
      shortDescription: 'Un corredor infernal de lava, destellos rojos y un pulso oscuro...',
      longDescription: 'En lo más profundo de una grieta ardiente, donde el suelo respira fuego y las sombras vibran con energía inestable, se encuentra la Guarida de Kan. Un corredor infernal de lava, destellos rojos y un pulso oscuro que marcaba la presencia de la criatura. Aquí los miembros de TECNILAND libraron una batalla brutal contra Kan, un monstruo colosal cuyo poder deformaba el flujo dimensional. Su derrota permitió cortar la conexión que atraía a la bestia hacia el mundo… pero las cicatrices de este lugar aún siguen vivas.',
      category: 'Cuevas',
      imageUrl: kan,
    },
    {
      id: 2,
      title: 'Valle del Alba Invernal',
      icon: 'landscape',
      shortDescription: 'Un valle extenso donde la nieve, los bosques y la luz del amanecer conviven en armonía.',
      longDescription: 'Este valle se despliega entre colinas nevadas, bosques de pinos y llanuras teñidas por el suave tono rosado de la vegetación estacional. Al amanecer, una neblina dorada cubre el horizonte, creando un ambiente cálido que contrasta con el frío de las montañas. Los exploradores de TECNILAND relatan que en estas tierras la magia fluye de manera natural: criaturas pacíficas recorren los bosques, y antiguos senderos llevan a puntos donde la energía del mundo se siente más viva. Es un lugar ideal para comenzar expediciones, estudiar el clima del Nexoverso o simplemente contemplar la belleza intacta de un territorio que aún no ha sido tocado por el caos dimensional.',
      category: 'Biomas',
      imageUrl: paisaje1,
    },
    {
      id: 3,
      title: 'El Vacío',
      icon: 'void',
      shortDescription: 'Un territorio donde la luz casi no existe y las sombras parecen tener vida propia.',
      longDescription: 'Este lugar es un fragmento olvidado entre dimensiones, donde la oscuridad domina por completo. Solo pequeñas motas de luz verde flotan en el aire, como si el propio mundo intentara recordar que existe algo más allá del vacío. Plataformas antiguas suspendidas en penumbra forman un camino inestable hacia lo desconocido. Quienes han entrado aquí afirman escuchar susurros que no provienen de ninguna criatura viva. Es una zona de exploración peligrosa pero esencial para comprender cómo el Abyss distorsiona la realidad en TECNILAND.',
      category: 'Mundos',
      imageUrl: vacio,
    },
    {
      id: 4,
      title: 'El Wither Storm',
      icon: 'bruno',
      shortDescription: 'Una entidad descomunal que distorsiona el cielo y fragmenta la realidad a su paso.',
      longDescription: 'Esta criatura, conocida entre los demonios como "Storm", abre grietas dimensionales dondequiera que aparece. Sus tentáculos envueltos en energía carmesí fracturan el aire como si desgarraran el espacio mismo, mientras un vórtice oscuro gira sobre su cuerpo, consumiendo trozos del entorno. La radiación rosada que emite altera la materia cercana, deformando bosques, colinas y cielo. Cada aparición es una catástrofe: señales de que el Nexoverso está debilitándose. Enfrentarlo es casi imposible… pero ignorarlo también lo es..',
      category: 'Monstruos',
      imageUrl: storm,
    },
    {
      id: 5,
      title: 'Pueblo de Tecniland',
      icon: 'city',
      shortDescription: 'Un asentamiento fortificado donde todo comenzó.',
      longDescription: 'Un pueblo redondo, protegido por murallas de piedra y coronado por una torre central que vigila cada rincón. Sus casas de madera iluminadas dan vida a un entorno pacífico rodeado de ríos y bosques. Es el corazón histórico de Tecniland: el primer refugio, el primer comercio, el primer punto seguro antes de que el caos del Nexoverso despertara.',
      category: 'Estructuras',
      imageUrl: pueblo,
    },
    {
      id: 6,
      title: 'Castillo En El Cielo',
      icon: 'castle-floating',
      shortDescription: 'Una fortaleza sagrada suspendida entre nubes antiguas.',
      longDescription: 'Un coloso flotante construido en capas ascendentes, cubierto de raíces, musgo y árboles que resisten al viento eterno. Desde abajo, su silueta domina el cielo como un guardián olvidado. Se dice que dentro del castillo reposan tecnologías y rituales tan viejos como el Nexoverso mismo, esperando a quienes sean dignos de ascender a sus alturas. Los Forjadores que han logrado llegar cuentan historias de pasillos interminables, cámaras llenas de artefactos brillantes y un aura de poder que impregna cada piedra. Sin embargo, también advierten sobre trampas antiguas y guardianes espectrales que protegen los secretos del castillo con fervor implacable.',
      category: 'Estructuras',
      imageUrl: castillosky,
    },
    {
      id: 7,
      title: 'THORMENTO',
      icon: 'celestial',
      shortDescription: 'Una aberración colosal cuyas sombras pueden eclipsar el cielo.',
      longDescription: 'Thorménto es una criatura arácnida de proporciones imposibles. Desde su núcleo ardiente cuelga una esfera incandescente que consume la luz y deforma el entorno. Sus extremidades se extienden como pilares macabros sobre montañas enteras, proyectando un terror que retumba incluso en el Nexoverso. Enfrentarlo es presenciar la esencia pura del miedo: un ser que no solo derrota, sino que corrompe aquello que toca. Su presencia es una señal de que las fuerzas del Vacío están ganando terreno, y su caída es crucial para restaurar el equilibrio en TECNILAND.',
      category: 'Monstruos',
      imageUrl: thormento,
    },
  ];

  // Social links
  const socialLinks = [
    {
      id: 1,
      name: 'Discord',
      icon: 'discord',
      link: 'https://discord.gg/eDmCZnc8tv',
      color: '#5865F2',
    },
    {
      id: 2,
      name: 'GitHub',
      icon: 'github',
      link: 'https://github.com/Ppkeash',
      color: '#333333',
    },
    {
      id: 3,
      name: 'Twitter',
      icon: 'twitter',
      link: 'https://x.com/UniLekConz',
      color: '#000000',
    },
    {
      id: 4,
      name: 'YouTube',
      icon: 'youtube',
      link: 'https://www.youtube.com/@lil_kysh',
      color: '#FF0000',
    },
    {
      id: 5,
      name: 'Email',
      icon: 'email',
      link: 'mailto:tutosmaspros@gmail.com',
      color: '#00ff00',
    },
  ];

  // ==================== HANDLERS ====================

  // Gallery handlers
  const nextSlide = () => {
    setCurrentGalleryIndex((prevIndex) => (prevIndex + 1) % screenshots.length);
  };

  const prevSlide = () => {
    setCurrentGalleryIndex((prevIndex) =>
      prevIndex === 0 ? screenshots.length - 1 : prevIndex - 1
    );
  };

  const goToSlide = (index) => {
    setCurrentGalleryIndex(index);
  };

  // Auto-advance carousel en galería (cada 30 segundos)
  useEffect(() => {
    if (selectedGalleryImage) return; // Pausar si modal abierto
    
    const interval = setInterval(() => {
      nextSlide();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [selectedGalleryImage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Contact form handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSubmitError('');
    setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateContactField = (fieldName, rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;

    if (fieldName === 'name') {
      if (!value) return '⚠️ Ingresa tu nombre.';
      if (value.length < 2) return '⚠️ Tu nombre es demasiado corto.';
      if (value.length > 60) return '⚠️ Tu nombre es demasiado largo.';
      return '';
    }

    if (fieldName === 'email') {
      if (!value) return '⚠️ Ingresa tu email.';
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) return '⚠️ Ingresa un email válido.';
      return '';
    }

    if (fieldName === 'subject') {
      if (!value) return '⚠️ Selecciona un asunto.';
      return '';
    }

    if (fieldName === 'message') {
      if (!value) return '⚠️ Escribe tu mensaje.';
      if (value.length < 10) return '⚠️ Tu mensaje es muy corto (mínimo 10 caracteres).';
      if (value.length > 2000) return '⚠️ Tu mensaje es muy largo (máximo 2000 caracteres).';
      return '';
    }

    return '';
  };

  const validateStage = (stage) => {
    const errors = { name: '', email: '', subject: '', message: '' };

    if (stage === 0) errors.name = validateContactField('name', formData.name);
    if (stage === 1) errors.email = validateContactField('email', formData.email);
    if (stage === 2) errors.subject = validateContactField('subject', formData.subject);
    if (stage === 3) errors.message = validateContactField('message', formData.message);

    const hasError = Object.values(errors).some(Boolean);
    return { errors, hasError };
  };

  const validateAllContactFields = () => {
    const errors = {
      name: validateContactField('name', formData.name),
      email: validateContactField('email', formData.email),
      subject: validateContactField('subject', formData.subject),
      message: validateContactField('message', formData.message),
    };
    const hasError = Object.values(errors).some(Boolean);
    return { errors, hasError };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitError('');

    const { errors, hasError } = validateAllContactFields();
    if (hasError) {
      setFieldErrors(errors);
      return;
    }

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    const toEmail = process.env.REACT_APP_CONTACT_TO_EMAIL || 'tutosmaspros@gmail.com';

    if (!serviceId || !templateId || !publicKey) {
      setSubmitError('EmailJS no está configurado. Revisa las variables REACT_APP_EMAILJS_* en tu .env.');
      return;
    }

    const subjectLabels = {
      colaboracion: 'Colaboración',
      consulta: 'Consulta Técnica',
      feedback: 'Feedback & Sugerencias',
      partnership: 'Partnership',
      otro: 'Otro',
    };

    const subjectLabel = subjectLabels[formData.subject] || formData.subject;

    try {
      setIsSending(true);

      await emailjs.send(
        serviceId,
        templateId,
        {
          to_email: toEmail,
          from_name: formData.name.trim(),
          reply_to: formData.email.trim(),
          user_email: formData.email.trim(),
          subject: subjectLabel,
          message: formData.message.trim(),
          source: 'TECNILAND Web',
        },
        { publicKey }
      );

      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setFieldErrors({ name: '', email: '', subject: '', message: '' });
        setFormStage(0);
      }, 3000);
    } catch (err) {
      setSubmitError('No se pudo enviar el mensaje. Intenta de nuevo en unos segundos.');
    } finally {
      setIsSending(false);
    }
  };

  const nextStage = (e) => {
    e.preventDefault();

    if (isSending) return;

    const { errors, hasError } = validateStage(formStage);
    if (hasError) {
      setFieldErrors((prev) => ({ ...prev, ...errors }));
      return;
    }

    if (formStage < 3) {
      setFormStage(formStage + 1);
    }
  };

  const prevStage = () => {
    if (formStage > 0) {
      setFormStage(formStage - 1);
    }
  };

  const currentScreenshot = screenshots[currentGalleryIndex];

  // ==================== RENDER ====================

  return (
    <div className={styles.homeUnified}>
      
      {/* ============ SECCIÓN HERO ============ */}
      <section id="hero" className={styles.heroSection}>
        <Banner />
        <Intro />
      </section>

      {/* ============ SECCIÓN CARACTERÍSTICAS (Launcher) ============ */}
      <SectionReveal id="features" className={`${styles.featuresSection} ${styles.launcher}`}>
        <SectionParticles count={25} color="#41B038" type="rise" intensity="medium" />
        <ScrollAnimatedSection animationType="fadeIn">
          <div className={styles.launcherHero}>
            <div className={styles.heroContent}>
              <h1 className={styles.heroTitle}><span translate="no" className="notranslate">TECNILAND</span> Nexus</h1>
              <p className={styles.heroSubtitle}>El Lanzador Oficial</p>
              <div className={styles.statusBadge}>
                <span className={styles.statusDot}></span>
                En Desarrollo (Alpha Cerrada para usuarios TECNILAND)
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className={styles.launcherTabs}>
          <div className={styles.tabsContainer}>
            <button
              className={`${styles.tabBtn} ${activeFeatureTab === 'vision' ? styles.active : ''}`}
              onClick={() => setActiveFeatureTab('vision')}
            >
              Visión
            </button>
            <button
              className={`${styles.tabBtn} ${activeFeatureTab === 'features' ? styles.active : ''}`}
              onClick={() => setActiveFeatureTab('features')}
            >
              Funcionalidades
            </button>
            <button
              className={`${styles.tabBtn} ${activeFeatureTab === 'roadmap-tech' ? styles.active : ''}`}
              onClick={() => setActiveFeatureTab('roadmap-tech')}
            >
              Tecnología
            </button>
          </div>
        </div>

        {/* Contenido por Tab */}
        <div className={styles.launcherContent}>
          {/* VISIÓN */}
          {activeFeatureTab === 'vision' && (
            <div className={`${styles.tabContent} ${styles.vision}`}>
              <h2>La Visión</h2>
              <p>
                Un launcher diseñado para ser la puerta oficial al universo de TECNILAND: rápido, estable y totalmente personalizado. Integrará actualización automática de modpacks, gestión de perfiles, optimización del rendimiento y una interfaz moderna que refleje la identidad del mundo. Su objetivo es brindar una experiencia fluida y centralizada donde todo —juego, contenido y comunidad— esté conectado en un solo lugar.
              </p>
              <div className={styles.visionGrid}>
                <div className={styles.visionCard}>
                  <h3>Accesibilidad</h3>
                  <p>Interfaz intuitiva para principiantes y control avanzado para usuarios expertos</p>
                </div>
                <div className={styles.visionCard}>
                  <h3>Expansión</h3>
                  <p>Un ecosistema que crece con tu creatividad</p>
                </div>
                <div className={styles.visionCard}>
                  <h3>Innovación</h3>
                  <p>Características únicas que no encontrarias tan facil en otros launchers</p>
                </div>
              </div>
            </div>
          )}

          {/* FUNCIONALIDADES */}
          {activeFeatureTab === 'features' && (
            <div className={`${styles.tabContent} ${styles.features}`}>
              <h2>Funcionalidades</h2>
              <div className={styles.featuresGrid}>
                {features.map((feature) => (
                  <div key={feature.id} className={styles.featureCard}>
                    <div className={styles.featureIcon}><Icon name={feature.icon} size="lg" animation="float" /></div>
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TECNOLOGÍA */}
          {activeFeatureTab === 'roadmap-tech' && (
            <div className={`${styles.tabContent} ${styles.roadmapTech}`}>
              <h2>Stack Tecnológico</h2>
              <div className={styles.techStack}>
                <div className={styles.techItem}>
                  <h3>⚙️ Electron</h3>
                  <p>Aplicación de escritorio multiplataforma</p>
                </div>
                <div className={styles.techItem}>
                  <h3>🟢 Node.js</h3>
                  <p>Backend robusto y eficiente</p>
                </div>
                <div className={styles.techItem}>
                  <h3>☕ Java Manager</h3>
                  <p>Gestión automática de versiones de Java</p>
                </div>
                <div className={styles.techItem}>
                  <h3>🔥 Forge Integration</h3>
                  <p>Soporte completo para Minecraft Forge 1.13-1.21.x</p>
                </div>
              </div>
            </div>
          )}
        </div>
        </ScrollAnimatedSection>
      </SectionReveal>

      {/* ============ SECCIÓN MODPACKS ============ */}
      <SectionReveal id="modpacks" className={`${styles.modpacksSection} ${styles.modpack}`}>
        <SectionParticles count={30} color="#41B038" type="mixed" intensity="high" />
        <ScrollAnimatedSection animationType="fadeIn">
        <div className={styles.modpackHero}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>M O D P A C K S</h1>
            <p className={styles.heroSubtitle}>Experiencias temáticas únicas y personalizadas</p>
          </div>
        </div>

        <div className={styles.modpackContainer}>
          <div className={styles.modpackGrid}>
            {modpacks.map((modpack) => (
              <div
                key={modpack.id}
                className={styles.modpackCard}
                onClick={() => setSelectedModpack(modpack)}
              >
                <div className={styles.cardImage}>
                  {modpack.image
                    ? <img src={modpack.image} alt={modpack.name} className={styles.cardImageWebp} />
                    : <Icon name={modpack.icon} size="xl" animation="pulse" />
                  }
                </div>
                <div className={styles.cardContent}>
                  <h3 translate="no" className="notranslate">{modpack.name}</h3>
                  <span className={styles.cardStatusBadge}>{modpack.status}</span>
                  <p className={styles.cardDescription}>{modpack.description}</p>
                  <div className={styles.cardMeta}>
                    <span>📦 {modpack.mods} mods</span>
                    <span>v{modpack.version}</span>
                  </div>
                  <button className={styles.btnView}>Ver Detalles</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        </ScrollAnimatedSection>
      </SectionReveal>

      {/* ============ SECCIÓN LORE ============ */}
      <SectionReveal id="lore" className={`${styles.loreSection} ${styles.lore}`}>
        <SectionParticles count={20} color="#00cc88" type="glow" intensity="medium" />
        <ScrollAnimatedSection animationType="fadeIn">
        <div className={styles.loreHero}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>El Lore de TECNILAND</h1>
            <p className={styles.heroSubtitle}>Un universo de infinitas posibilidades</p>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className={styles.loreTabs}>
          <div className={styles.tabsContainer}>
            <button
              className={`${styles.tabBtn} ${activeLoreTab === 'intro' ? styles.active : ''}`}
              onClick={() => setActiveLoreTab('intro')}
            >
              El Universo
            </button>
            <button
              className={`${styles.tabBtn} ${activeLoreTab === 'characters' ? styles.active : ''}`}
              onClick={() => setActiveLoreTab('characters')}
            >
              Forjadores
            </button>
            <button
              className={`${styles.tabBtn} ${activeLoreTab === 'personajes' ? styles.active : ''}`}
              onClick={() => setActiveLoreTab('personajes')}
            >
              Personajes
            </button>
            <button
              className={`${styles.tabBtn} ${activeLoreTab === 'missions' ? styles.active : ''}`}
              onClick={() => setActiveLoreTab('missions')}
            >
              Crónicas
            </button>
          </div>
        </div>

        {/* Contenido por Tab */}
        <div className={styles.loreContent}>
          {/* EL UNIVERSO */}
          {activeLoreTab === 'intro' && (
            <div className={`${styles.tabContent} ${styles.intro}`}>
              <h2>El Origen</h2>
              <p>
                <span className={styles.loreKeyword}>TECNILAND</span> nació del colapso de un mundo entero. Un superviviente —<span className={styles.lorePurple}>Sorin</span>— escapó de las ruinas de su realidad a través de un portal desconocido. Lo que encontró al otro lado no fue solo un nuevo hogar… sino un fragmento perdido del <span className={styles.loreKeyword}>Nexoverso</span>, un conjunto de mundos conectados por los <span className={styles.loreKeyword}>Nexus</span>, corazones de energía capaces de sostener o destruir universos completos.
              </p>
              <p>
                Pero el portal no venía solo. Detrás de <span className={styles.lorePurple}>Sorin</span>, una <span className={styles.lorePurple}>criatura indescriptible</span> cruzó la brecha y dejó cicatrices en cada mundo por el que pasó. Una tormenta eterna, un rugido que nadie podía oír… excepto aquellos marcados por el destino.
              </p>
              <div className={styles.loreHighlightBox}>
                Hoy <span className={styles.loreKeyword}>TECNILAND</span> es el último bastión que queda en pie. Un mundo donde la magia, la tecnología, la memoria y la oscuridad chocan constantemente.
              </div>
              <p>
                El <span className={styles.loreKeyword}>Nexus</span> de este mundo sigue activo, protegido por <span className={styles.lorePurple}>Eryndra</span>… pero inestable. Y el misterio del <span className={styles.lorePurple}>Abyss</span> —una dimensión muerta que guarda la <span className={styles.loreKeyword}>Gema del Recuerdo</span>— es la única pista para entender qué arrasó las demás realidades… y quizá, para encontrar al propio <span className={styles.lorePurple}>Sorin</span>.
              </p>
              <p style={{ textAlign: 'center', marginTop: '2rem', fontStyle: 'italic', color: 'var(--color-primary)' }}>
                Aquí empieza la historia de quienes se atrevan a entrar.
              </p>
              <div className={styles.nexusCard}>
                <h3>El Nexus</h3>
                <p>
                  El corazón del multiverso. Un portal infinito que conecta todas las dimensiones.
                  Solo unos pocos han visto su verdadero poder.
                </p>
              </div>
            </div>
          )}

          {/* PERSONAJES */}
          {activeLoreTab === 'characters' && (
            <div className={`${styles.tabContent} ${styles.characters}`}>
              <h2>Los Forjadores</h2>
              <div className={styles.charactersGrid}>
                {characters.map((character) => (
                  <div
                    key={character.id}
                    className={styles.characterCard}
                    onClick={() => setSelectedCharacter(character)}
                  >
                    <div className={styles.characterEmoji}><Icon name={character.icon} size="xl" animation="float" /></div>
                    <h3>{character.name}</h3>
                    <p className={styles.characterRole}>{character.role}</p>
                    {character.featuredMission && (
                      <p className={styles.featuredMission}>{character.featuredMission}</p>
                    )}
                    <p className={styles.characterDescription}>{character.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PERSONAJES DEL LORE */}
          {activeLoreTab === 'personajes' && (
            <div className={`${styles.tabContent} ${styles.personajes}`}>
              <h2>Personajes del Universo</h2>
              <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '2rem' }}>
                Figuras legendarias que habitan el Nexoverso. Cada uno guarda un secreto del multiverso.
              </p>
              <div className={styles.personajesGrid}>
                {lorePersonajes.map((personaje) => (
                  <div 
                    key={personaje.id} 
                    className={styles.personajeCard}
                    onClick={() => setSelectedPersonaje(personaje)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className={styles.personajeIcon}><Icon name={personaje.icon} size="xl" animation="pulse" /></div>
                    <h3>{personaje.name}</h3>
                    <p className={styles.personajeImportance}>{personaje.importance}</p>
                    <p className={styles.personajeDescription}>{personaje.description}</p>
                    <span className={styles.personajeHighlight}>{personaje.highlight}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* MISIONES */}
          {activeLoreTab === 'missions' && (
            <div className={`${styles.tabContent} ${styles.missions}`}>
              <h2>Las Crónicas</h2>
              <div className={styles.missionsGrid}>
                {missions.map((mission) => (
                  <div 
                    key={mission.id} 
                    className={styles.missionCard}
                    onClick={() => setSelectedMission(mission)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className={styles.missionEmoji}><Icon name={mission.icon} size="lg" animation="float" /></div>
                    <h3>{mission.title}</h3>
                    <p>{mission.description}</p>
                    <span className={`${styles.missionStatus} ${styles[mission.status.toLowerCase().replace(' ', '')]}`}>
                      {mission.status}
                    </span>
                    <p className={styles.missionReward}><strong>Recompensa:</strong> {mission.reward}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        </ScrollAnimatedSection>
      </SectionReveal>

      {/* ============ SECCIÓN ROADMAP ============ */}
      <SectionReveal id="roadmap" className={`${styles.roadmapSection} ${styles.roadmap}`}>
        <SectionParticles count={22} color="#88ff00" type="float" intensity="medium" />
        <ScrollAnimatedSection animationType="fadeIn">
        <div className={styles.roadmapHero}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>Roadmap de TECNILAND</h1>
            <p className={styles.heroSubtitle}>El futuro del multiverso está aquí</p>
          </div>
        </div>

        <div className={styles.visionSection}>
          <div className={styles.visionContainer}>
            <div className={styles.visionCard}>
              <h2>Nuestra Visión</h2>
              <p>
                Construir un mundo vivo dentro de Minecraft, con su propio lore, personajes, misiones, eventos, modpacks personalizados y hasta un launcher oficial. Un lugar donde cada jugador pueda entrar, crear su historia y dejar huella en una narrativa que evoluciona con la comunidad.
              </p>
            </div>
          </div>
        </div>

        <div className={styles.timelineSection}>
          <div className={styles.timelineContainer}>
            <h2>Línea de Tiempo</h2>

            <div className={styles.timeline}>
              {roadmapItems.map((item, index) => (
                <div key={item.id} className={styles.timelineItem}>
                  {index !== roadmapItems.length - 1 && (
                    <div
                      className={styles.timelineLine}
                      style={{ background: `linear-gradient(180deg, ${item.color} 0%, transparent 100%)` }}
                    ></div>
                  )}

                  <div className={styles.timelineCard} style={{ borderColor: item.color }}>
                    <div className={styles.cardHeader} style={{ borderBottomColor: item.color }}>
                      <span className={styles.phaseLabel}>{item.phase}</span>
                      <span className={styles.phaseEmoji}><Icon name={item.icon} size="lg" animation="bounce" /></span>
                    </div>

                    <div className={styles.cardContent}>
                      <h3>{item.title}</h3>
                      <p className={styles.cardDate}>{item.date}</p>
                      <span
                        className={`${styles.statusBadge} ${styles[item.status.toLowerCase().replace(' ', '')]}`}
                        style={{
                          '--status-color': item.color,
                        }}
                      >
                        {item.status}
                      </span>
                    </div>

                    <div className={styles.cardMilestones}>
                      <button
                        className={styles.expandBtn}
                        onClick={() => setExpandedRoadmapItem(expandedRoadmapItem === item.id ? null : item.id)}
                        style={{ color: item.color }}
                      >
                        {expandedRoadmapItem === item.id ? '−' : '+'} Hitos ({item.milestones.length})
                      </button>

                      {expandedRoadmapItem === item.id && (
                        <ul className={styles.milestonesList}>
                          {item.milestones.map((milestone, idx) => (
                            <li key={idx}>{milestone}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        </ScrollAnimatedSection>
      </SectionReveal>

      {/* ============ SECCIÓN GALERÍA ============ */}
      <SectionReveal id="gallery" className={`${styles.gallerySection} ${styles.gallery}`}>
        <SectionParticles count={18} color="#00cc88" type="rise" intensity="low" />
        <ScrollAnimatedSection animationType="fadeIn">
        <div className={styles.galleryHero}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>Galería de TECNILAND</h1>
            <p className={styles.heroSubtitle}>Explora los mundos que conforman nuestro multiverso</p>
          </div>
        </div>

        {/* Main Carousel con Background Images */}
        <div className={styles.carouselSection}>
          <div className={styles.carouselContainer}>
            {/* Panel principal con background */}
            <div className={styles.carouselMain}>
              {/* Background Image Carousel - SOLO dentro del panel principal */}
              <div className={styles.carouselBackground}>
                {screenshots.map((screenshot, index) => (
                  <div
                    key={screenshot.id}
                    className={`${styles.backgroundSlide} ${index === currentGalleryIndex ? styles.active : ''}`}
                    style={{
                      backgroundImage: screenshot.imageUrl ? `url(${screenshot.imageUrl})` : 'none',
                    }}
                    aria-hidden={index !== currentGalleryIndex}
                  />
                ))}
                <div className={styles.backgroundOverlay} />
              </div>

              {/* Content sobre el background */}
              <div className={styles.mainContent}>
                <div className={styles.mainImage}>
                  <span className={styles.imageEmoji}><Icon name={currentScreenshot.icon} size="xl" animation="float" /></span>
                </div>
                <div className={styles.slideInfo}>
                  <span className={styles.slideCategory}>{currentScreenshot.category}</span>
                  <h2 className={styles.slideTitle}>{currentScreenshot.title}</h2>
                  <p className={styles.slideDescription}>{currentScreenshot.shortDescription}</p>
                  <div className={styles.slideCounter}>
                    {currentGalleryIndex + 1} / {screenshots.length}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.carouselControls}>
              <button className={`${styles.controlBtn} ${styles.prev}`} onClick={prevSlide} title="Anterior" aria-label="Imagen anterior">
                ←
              </button>
              <button className={`${styles.controlBtn} ${styles.next}`} onClick={nextSlide} title="Siguiente" aria-label="Siguiente imagen">
                →
              </button>
            </div>
          </div>
        </div>

        {/* Thumbnails */}
        <div className={styles.thumbnailsSection}>
          <div className={styles.thumbnailsContainer}>
            <div className={styles.thumbnailsGrid}>
              {screenshots.map((screenshot, index) => (
                <div
                  key={screenshot.id}
                  className={`${styles.thumbnail} ${index === currentGalleryIndex ? styles.active : ''}`}
                  onClick={() => goToSlide(index)}
                  style={{
                    backgroundImage: `url(${screenshot.imageUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                >
                  <div className={styles.thumbnailEmoji}><Icon name={screenshot.icon} size="md" animation="float" /></div>
                  <p className={styles.thumbnailTitle}>{screenshot.title}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Grid Gallery */}
        <div className={styles.gridSection}>
          <div className={styles.gridContainer}>
            <h2>Vista de Galería Completa</h2>
            <div className={styles.galleryGrid}>
              {screenshots.map((screenshot) => (
                <div
                  key={screenshot.id}
                  className={styles.galleryItem}
                  onClick={() => setSelectedGalleryImage(screenshot)}
                  style={{
                    backgroundImage: `url(${screenshot.imageUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                >
                  <div className={styles.itemEmoji}><Icon name={screenshot.icon} size="xl" animation="float" /></div>
                  <div className={styles.itemInfo}>
                    <h3>{screenshot.title}</h3>
                    <span className={styles.itemCategory}>{screenshot.category}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        </ScrollAnimatedSection>
      </SectionReveal>

      {/* ============ SECCIÓN CONTACTO ============ */}
      <SectionReveal id="contact" className={`${styles.contactSection} ${styles.contact}`}>
        <SectionParticles count={20} color="#41B038" type="mixed" intensity="medium" />
        <ScrollAnimatedSection animationType="fadeIn">
        <div className={styles.contactHero}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>Únete a TECNILAND</h1>
            <p className={styles.heroSubtitle}>Una decisión que cambiará tu viaje</p>
          </div>
        </div>

        <div className={styles.dramaSection}>
          <div className={styles.dramaContainer}>
            <div className={styles.dramaCard}>
              <h2><Icon name="zap" size="md" /> Este es un momento importante</h2>
              <p>
                No es solo un formulario. Es la puerta. Al contactarnos, te unes a la visión de TECNILAND.
                Gente como tú, que ve más allá. Que sueña en grande. Que construye mundos.
              </p>
            </div>
          </div>
        </div>

        {/* Formulario */}
        <div className={styles.formSection}>
          <div 
            className={styles.formContainer}
            style={{
              background: 'rgba(10, 14, 39, 0.7)',
              backdropFilter: 'blur(8px)',
              border: '2px solid rgba(65, 176, 56, 0.25)',
              borderRadius: '12px',
              padding: '2rem',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
            }}
          >
            {!submitted ? (
              <>
                <div className={styles.formHeader}>
                  <h2>Envía tu Mensaje</h2>
                  <p className={styles.formSubtitle}>
                    Comparte tu visión, tus ideas, tu intención. Leemos cada mensaje.
                  </p>
                  {submitError && (
                    <div className={styles.submitError} role="alert">
                      {submitError}
                    </div>
                  )}
                </div>

                <div className={styles.progressBar}>
                  <div className={styles.progressFill} style={{ width: `${(formStage / 3) * 100}%` }}></div>
                </div>

                <form onSubmit={handleSubmit} className={styles.contactForm}>
                  {/* Stage 0: Nombre */}
                  {formStage === 0 && (
                    <div className={styles.formStage}>
                      <div className={styles.stageContent}>
                        <h3>¿Cuál es tu nombre?</h3>
                        <input
                          type="text"
                          name="name"
                          placeholder="Tu nombre..."
                          value={formData.name}
                          onChange={handleChange}
                          className={`${styles.formInput} ${fieldErrors.name ? styles.inputError : ''}`}
                          aria-invalid={Boolean(fieldErrors.name)}
                          aria-describedby={fieldErrors.name ? 'contact-name-error' : undefined}
                          disabled={isSending}
                          autoFocus
                        />
                        {fieldErrors.name && (
                          <p id="contact-name-error" className={styles.fieldError} role="alert">
                            {fieldErrors.name}
                          </p>
                        )}
                        <p className={styles.stageHint}>
                          Queremos saber quién eres. Cada nombre representa una posibilidad en TECNILAND.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Stage 1: Email */}
                  {formStage === 1 && (
                    <div className={styles.formStage}>
                      <div className={styles.stageContent}>
                        <h3>¿Cómo te contactamos?</h3>
                        <input
                          type="email"
                          name="email"
                          placeholder="tu@email.com"
                          value={formData.email}
                          onChange={handleChange}
                          className={`${styles.formInput} ${fieldErrors.email ? styles.inputError : ''}`}
                          aria-invalid={Boolean(fieldErrors.email)}
                          aria-describedby={fieldErrors.email ? 'contact-email-error' : undefined}
                          disabled={isSending}
                          autoFocus
                        />
                        {fieldErrors.email && (
                          <p id="contact-email-error" className={styles.fieldError} role="alert">
                            {fieldErrors.email}
                          </p>
                        )}
                        <p className={styles.stageHint}>
                          Tu email es el puente entre nosotros y tu futura aventura.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Stage 2: Subject */}
                  {formStage === 2 && (
                    <div className={styles.formStage}>
                      <div className={styles.stageContent}>
                        <h3>¿Cuál es tu intención?</h3>
                        <select
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          className={`${styles.formInput} ${fieldErrors.subject ? styles.inputError : ''}`}
                          aria-invalid={Boolean(fieldErrors.subject)}
                          aria-describedby={fieldErrors.subject ? 'contact-subject-error' : undefined}
                          disabled={isSending}
                          autoFocus
                        >
                          <option value="">Selecciona una opción...</option>
                          <option value="colaboracion">Colaboración</option>
                          <option value="consulta">Consulta Técnica</option>
                          <option value="feedback">Feedback & Sugerencias</option>
                          <option value="partnership">Partnership</option>
                          <option value="otro">Otro</option>
                        </select>
                        {fieldErrors.subject && (
                          <p id="contact-subject-error" className={styles.fieldError} role="alert">
                            {fieldErrors.subject}
                          </p>
                        )}
                        <p className={styles.stageHint}>
                          Cada intención es valiosa. Somos toda oídos.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Stage 3: Mensaje */}
                  {formStage === 3 && (
                    <div className={styles.formStage}>
                      <div className={styles.stageContent}>
                        <h3>Cuéntanos tu historia</h3>
                        <textarea
                          name="message"
                          placeholder="Escribe tu mensaje aquí..."
                          value={formData.message}
                          onChange={handleChange}
                          className={`${styles.formInput} ${styles.textarea} ${fieldErrors.message ? styles.inputError : ''}`}
                          aria-invalid={Boolean(fieldErrors.message)}
                          aria-describedby={fieldErrors.message ? 'contact-message-error' : undefined}
                          disabled={isSending}
                          rows="6"
                          autoFocus
                        ></textarea>
                        {fieldErrors.message && (
                          <p id="contact-message-error" className={styles.fieldError} role="alert">
                            {fieldErrors.message}
                          </p>
                        )}
                        <p className={styles.stageHint}>
                          Aquí es donde todo cambia. Tu mensaje importa.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Botones de navegación */}
                  <div className={styles.formNavigation}>
                    {formStage > 0 && (
                      <button type="button" className={styles.btnPrev} onClick={prevStage} disabled={isSending}>
                        ← Anterior
                      </button>
                    )}
                    {formStage < 3 && (
                      <button type="button" className={styles.btnNext} onClick={nextStage} disabled={isSending}>
                        Siguiente →
                      </button>
                    )}
                    {formStage === 3 && (
                      <button type="submit" className={`${styles.btnSubmit} ${isSending ? styles.btnDisabled : ''}`} disabled={isSending}>
                        {isSending ? 'Enviando…' : 'Enviar Mensaje'} <Icon name="sparkles" size="sm" />
                      </button>
                    )}
                  </div>
                </form>
              </>
            ) : (
              <div className={styles.successMessage}>
                <div className={styles.successIcon}>✅</div>
                <h2>¡Mensaje Enviado!</h2>
                <p>
                  Gracias por unirte a la visión. Te responderemos pronto.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Social Links */}
        <div className={styles.socialSection}>
          <div className={styles.socialContainer}>
            <h2>También puedes encontrarnos aquí</h2>
            <div className={styles.socialLinks}>
              {socialLinks.map((social) => (
                <a
                  key={social.id}
                  href={social.link}
                  className={styles.socialLink}
                  style={{ '--social-color': social.color }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className={styles.socialEmoji}><Icon name={social.icon} size="sm" animation="float" /></span>
                  <span className={styles.socialName}>{social.name}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
        </ScrollAnimatedSection>
      </SectionReveal>

      {/* ============ MODALES (fuera de secciones para position:fixed correcto) ============ */}
      
      {/* Lightbox de Gallery */}
      {selectedGalleryImage && (
        <div 
          ref={galleryModalRef} 
          className={`${styles.lightboxOverlay} ${!showLightboxText ? styles.imageMode : ''}`}
          onClick={() => setSelectedGalleryImage(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`Imagen: ${selectedGalleryImage.title}`}
          style={{
            backgroundImage: `url(${selectedGalleryImage.imageUrl})`,
            backgroundSize: !showLightboxText ? 'contain' : 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <button 
            className={styles.lightboxToggleBtn} 
            onClick={(e) => {
              e.stopPropagation();
              setShowLightboxText(!showLightboxText);
            }}
            aria-label={showLightboxText ? "Ver imagen completa" : "Mostrar información"}
          >
            <Icon name={showLightboxText ? "image" : "text"} size="sm" />
            <span>{showLightboxText ? "Ver imagen" : "Leer texto"}</span>
          </button>

          {!showLightboxText && (
            <button 
              className={styles.lightboxCloseImageMode} 
              onClick={() => setSelectedGalleryImage(null)}
              title="Cerrar"
              aria-label="Cerrar visor de imagen"
            >
              <Icon name="x" size="md" />
            </button>
          )}

          {showLightboxText && (
            <div className={styles.lightboxContent} onClick={(e) => e.stopPropagation()}>
              <button className={styles.lightboxClose} onClick={() => setSelectedGalleryImage(null)} aria-label="Cerrar">
                <Icon name="x" size="sm" />
              </button>
              <div className={styles.lightboxImage}>
                <span className={styles.lightboxEmoji}><Icon name={selectedGalleryImage.icon} size="xl" /></span>
              </div>
              <div className={styles.lightboxInfo}>
                <h2>{selectedGalleryImage.title}</h2>
                <p>{selectedGalleryImage.longDescription}</p>
                <span className={styles.lightboxCategory}>{selectedGalleryImage.category}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de Modpack */}
      {selectedModpack && (
        <div 
          ref={modpackModalRef} 
          className={styles.modalOverlay} 
          onClick={() => setSelectedModpack(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modpack-modal-title"
        >
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={() => setSelectedModpack(null)} aria-label="Cerrar modal">
              <Icon name="x" size="sm" />
            </button>
            <div className={styles.modalHeader}>
              <div className={styles.modalImage}>
                {selectedModpack.image
                  ? <img src={selectedModpack.image} alt={selectedModpack.name} className={styles.modalImageWebp} />
                  : <Icon name={selectedModpack.icon} size="xl" />
                }
              </div>
              <div className={styles.modalTitleSection}>
                <h2 id="modpack-modal-title">{selectedModpack.name}</h2>
                <span className={styles.modalStatus}>{selectedModpack.status}</span>
              </div>
            </div>
            <div className={styles.modalBody}>
              <p>{selectedModpack.description}</p>
              <h3>Características:</h3>
              <ul>
                {selectedModpack.features.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>
              <div className={styles.modalMeta}>
                <p><strong>Versión:</strong> {selectedModpack.version}</p>
                <p><strong>Total de Mods:</strong> {selectedModpack.mods}</p>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.btnDownload}>Descargar Modpack</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Character (LORE) */}
      {selectedCharacter && (
        <div 
          ref={characterModalRef} 
          className={styles.modalOverlay} 
          onClick={() => setSelectedCharacter(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="character-modal-title"
        >
          <div className={`${styles.modalContent} ${styles.characterModal}`} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={() => setSelectedCharacter(null)} aria-label="Cerrar modal">
              <Icon name="x" size="sm" />
            </button>
            <div className={styles.modalHeader}>
              <div className={styles.modalEmoji}><Icon name={selectedCharacter.icon} size="xl" /></div>
              <div className={styles.modalTitleSection}>
                <h2 id="character-modal-title">{selectedCharacter.name}</h2>
                <p className={styles.modalRole}>{selectedCharacter.role}</p>
                {selectedCharacter.featuredMission && (
                  <div className={styles.featuredMissionModal}>
                    <span className={styles.featuredMissionLabel}><Icon name="zap" size="sm" /> Misión destacada:</span>
                    <span className={styles.featuredMissionText}>{selectedCharacter.featuredMission}</span>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.modalBody}>
              <h3>Historia</h3>
              <p>{selectedCharacter.story}</p>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Personaje */}
      {selectedPersonaje && (
        <div 
          ref={personajeModalRef} 
          className={styles.modalOverlay} 
          onClick={() => setSelectedPersonaje(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="personaje-modal-title"
        >
          <div className={`${styles.modalContent} ${styles.personajeModal}`} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={() => setSelectedPersonaje(null)} aria-label="Cerrar modal">
              <Icon name="x" size="sm" />
            </button>
            <div className={styles.modalHeader}>
              <div className={styles.modalEmoji}><Icon name={selectedPersonaje.icon} size="xl" /></div>
              <div className={styles.modalTitleSection}>
                <h2 id="personaje-modal-title">{selectedPersonaje.name}</h2>
                <p className={styles.modalRole} style={{ color: '#b794f6' }}>{selectedPersonaje.importance}</p>
                <div className={styles.personajeHighlight} style={{ marginTop: '1rem' }}>
                  {selectedPersonaje.highlight}
                </div>
              </div>
            </div>
            <div className={styles.modalBody}>
              <h3>Historia</h3>
              <p>{selectedPersonaje.story}</p>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Misión */}
      {selectedMission && (
        <div 
          ref={missionModalRef} 
          className={styles.modalOverlay} 
          onClick={() => setSelectedMission(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="mission-modal-title"
        >
          <div className={`${styles.modalContent} ${styles.missionModal}`} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={() => setSelectedMission(null)} aria-label="Cerrar modal">
              <Icon name="x" size="sm" />
            </button>
            <div className={styles.modalHeader}>
              <div className={styles.modalEmoji}><Icon name={selectedMission.icon} size="xl" /></div>
              <div className={styles.modalTitleSection}>
                <h2 id="mission-modal-title">{selectedMission.title}</h2>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '0.5rem' }}>
                  <span className={`${styles.missionStatus} ${styles[selectedMission.status.toLowerCase().replace(' ', '')]}`}>
                    {selectedMission.status}
                  </span>
                  <p className={styles.missionReward} style={{ margin: 0 }}>
                    <strong>Recompensa:</strong> {selectedMission.reward}
                  </p>
                </div>
              </div>
            </div>
            <div className={styles.modalBody}>
              <h3>Historia</h3>
              <p>{selectedMission.story}</p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Home;
